import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Section, SectionHeading, Eyebrow, Icon, PrimaryLink } from "@/components/common";
import { PILLARS, BROCHURE } from "@/data/content";
import { CapabilityDiagram, ServiceVisual, PortfolioSection, ProcessGraphic } from "@/components/BrandVisuals";
import { useLang } from "@/context/LanguageContext";

const FILTERS = [
  { id: "all", label: "All capabilities" },
  ...PILLARS.map((p) => ({ id: p.id, label: p.name })),
];

export default function Services() {
  const { t } = useLang();
  const location = useLocation();
  const hashPillar = PILLARS.find((p) => `#${p.id}` === location.hash)?.id;
  const [active, setActive] = useState(() => hashPillar || "all");

  useEffect(() => {
    setActive(hashPillar || "all");
  }, [hashPillar, location.key]);

  useEffect(() => {
    if (!hashPillar || active !== hashPillar) return;
    const scrollToPillar = () => document.getElementById(`service-${hashPillar}`)?.scrollIntoView({ behavior: "instant", block: "start" });
    const frame = requestAnimationFrame(scrollToPillar);
    window.addEventListener("load", scrollToPillar);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("load", scrollToPillar);
    };
  }, [hashPillar, active, location.key]);

  const visible = active === "all" ? PILLARS : PILLARS.filter((p) => p.id === active);

  return (
    <div>
      <Seo title="Services" description="Explore ERAM DIGITAL's AI, software, cloud and managed services, plus PIMS, MES, SCADA, IT-OT, process safety and industrial automation capabilities." path="/services" />

      <Section className="py-20">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Services</Eyebrow>
            <h1 className="max-w-4xl font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">From sensor<br />to <span className="gradient-text">boardroom.</span></h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">{BROCHURE.intro}</p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">Connect your plant-floor and enterprise systems, from strategy through implementation and support.</p>
          </Reveal>
          <Reveal delay={0.1}><CapabilityDiagram /></Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="services-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setActive(f.id)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                  active === f.id ? "gradient-bg text-white" : "border border-[#E5E7EB] text-[#4B5563] hover:text-[#0A0A0A]"
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>
      </Section>

      <Section className="pb-20">
        <div data-testid="service-results">
          <div className="space-y-8">
            {visible.map((p, i) => (
              <motion.div
                key={p.id}
                id={`service-${p.id}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="scroll-mt-24 overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white"
                data-testid={`service-card-${p.id}`}
              >
                <div className="grid lg:grid-cols-2">
                  <div className="p-8 sm:p-12">
                    <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl gradient-bg text-white"><Icon name={p.icon} className="h-7 w-7" /></div>
                    <h2 className="font-display text-3xl font-semibold text-[#0A0A0A]">{p.name}</h2>
                    <p className="mt-1 text-sm font-medium text-brand-violet">{p.tagline}</p>
                    <p className="mt-4 leading-relaxed text-[#4B5563]">{p.summary}</p>
                    <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                      {p.capabilities.map((c) => (
                        <li key={c} className="flex items-start gap-2 text-sm text-[#4B5563]">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" />{c}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] p-4">
                      <p className="text-xs font-medium uppercase tracking-widest text-brand-teal">Business outcome</p>
                      <p className="mt-1 text-sm text-[#0A0A0A]">{p.outcome}</p>
                    </div>
                  </div>
                  <ServiceVisual service={p} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="py-20">
        <SectionHeading eyebrow="Plant-to-enterprise capability stack" title="Built for critical" gradient="operations." subtitle="Operations → Intelligence → Resilience → Enterprise value" />
        <div className="mt-12 grid gap-x-10 md:grid-cols-2" data-testid="brochure-capabilities">
          {BROCHURE.capabilities.map((cap, i) => <div key={cap.id} className="flex gap-5 border-t border-slate-200 py-7" data-testid={`capability-${cap.id}`}>
            <span className="pt-1 text-xs font-semibold text-slate-400">0{i + 1}</span>
            <Icon name={cap.icon} className="mt-1 h-6 w-6 shrink-0 text-brand-violet" />
            <div><h3 className="font-display text-lg font-bold">{cap.name}</h3><p className="mt-2 text-sm leading-relaxed text-slate-600">{cap.summary}</p></div>
          </div>)}
        </div>
      </Section>
      <PortfolioSection />
      <ProcessGraphic />
      <Section className="py-16"><PrimaryLink to="/contact" testid="services-cta">{t("cta.consult")}</PrimaryLink></Section>
    </div>
  );
}
